<script lang="ts" setup>
import emptyOptionImage from "@/assets/img/picture-choice-empty.svg?url";
import otherOptionImage from "@/assets/img/picture-choice-other.svg?url";
import RichTextEditor from "@/components/text-editor/index.vue";
import { useAppStore } from "@/store/app";
import type { PictureChoiceOption } from "@/types/pictureChoice";
import {
  dateRangeShortcuts,
  dateTypeFieldFormatDayjsForCondition,
  getDateTypeFieldValue,
  getOptionIconVNode,
  includeEndText,
  includeStartText,
  parseConditionDateTimeValue,
  dateTypeFieldFormat as resolveDateTypeFieldFormat,
  dateTypeFieldFormatDayjs as resolveDateTypeFieldFormatDayjs,
  resolveConditionalFieldKind,
  shouldHideInput,
  timeFormatForUI,
  toConditionDateTimeValue,
  toConditionTimeValue,
} from "@/util/conditional-logic-helpers";
import { IS_EMPTY, IS_FILLED } from "@/util/constants";
import { toTimePickerTimestamp } from "@/util/dateTimeSerialize";
import type { GroupField } from "@/util/methods";
import { allOperationOptions, standardFields } from "@/util/methods";
import { isPictureChoiceField } from "@/util/pictureChoice";
import { Trash02Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIAlert,
  UICheckbox,
  UIDatepicker,
  UIInput,
  UIModalFooter,
  UISelect,
  UITabPane,
  UITabs,
  UITimePicker,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { cloneDeep } from "lodash";
import { VNode, computed, h, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

// Example URL constants (not translated - these are literal URL examples)
const URL_EXAMPLES = {
  simple: "example.com",
  https: "https://example.com",
  withPath: "example.com/path",
  fullPath: "https://www.example.com/path",
};

dayjs.extend(customParseFormat);
type FieldMode = "field" | "slide";
interface ConditionRow {
  selectedField: string | null;
  selectedOperation: string | null;
  inputValue: any;
  rangeStart?: any;
  rangeEnd?: any;
  includeStart: boolean;
  includeEnd: boolean;
  fieldMode?: FieldMode;
  dateFormat?: string;
}
/**
 * Builder element as consumed when building the condition field list. `GroupField`
 * does not declare these two element props, and widening the shared type is out of
 * scope here, so they are added locally where they are actually read.
 */
type ConditionalSourceField = GroupField & {
  placeholder2?: string;
  picklistOptionsImage?: Array<{ label: string }>;
};
interface ConditionalFieldOption {
  value: string;
  label: string;
  type: string;
  conditionalFieldKind?: string;
  dataType?: string;
  format?: string;
  separator?: string;
  timeFormat?: string | number;
  icon: () => VNode | null;
  picklistOptions?: string[] | null;
  picklistOptionsImage?: PictureChoiceOption[] | null;
  isPictureChoice?: boolean;
  allowCustomOption?: boolean;
}
const props = defineProps({
  editIndex: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "displayCustomMessage",
  },
});

const showPhoneAlert = ref(false);
const disqualifyActionOptions = [
  { label: t("form.conditonalLogic.showCustomMessage"), value: "showCustomMessage" },
  { label: t("common.openUrl"), value: "openUrl" },
];

const disqualifyTimingOptions = [
  { label: t("common.disqualifyImmediately"), value: "disqualifyImmediately" },
  { label: t("common.disqualifyAfterSubmit"), value: "disqualifyAfterSubmit" },
];
const showHideOptions = [
  { label: t("form.conditonalLogic.hideField"), value: "Hide" },
  { label: t("form.conditonalLogic.showField"), value: "Show" },
  { label: t("form.conditonalLogic.hideFields"), value: "Hide Multiple" },
  { label: t("form.conditonalLogic.showFields"), value: "Show Multiple" },
];
const showMultiple = computed(() => hideType.value.includes("Multiple"));

const store = useAppStore();
const emit = defineEmits(["onSave"]);
const sharedSelectedOperation = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].conditionalOperation || "then"
);
const disqualifyTiming = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.disqualifyTiming ||
    "disqualifyAfterSubmit"
);
const disqualifyAction = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.disqualifyAction ||
    "showCustomMessage"
);
const outcomeValue = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.value || ""
);
const hideType = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.hideType || "Hide"
);
const jumpTarget = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.jumpTarget || ""
);
const toConditionRow = (c: any): ConditionRow => {
  const isBetween = c?.selectedOperation === "between";
  let inputPayload = c?.inputValue;
  if (isBetween && typeof inputPayload === "string") {
    try {
      inputPayload = JSON.parse(inputPayload);
    } catch {
      inputPayload = undefined;
    }
  }

  const hydratedRangeStart =
    c?.rangeStart !== undefined && c?.rangeStart !== null && c?.rangeStart !== ""
      ? c.rangeStart
      : isBetween
      ? inputPayload?.start
      : undefined;
  const hydratedRangeEnd =
    c?.rangeEnd !== undefined && c?.rangeEnd !== null && c?.rangeEnd !== ""
      ? c.rangeEnd
      : isBetween
      ? inputPayload?.end
      : undefined;
  const hydratedIncludeStart =
    typeof c?.includeStart === "boolean"
      ? c.includeStart
      : isBetween && typeof inputPayload?.includeStart === "boolean"
      ? inputPayload.includeStart
      : true;
  const hydratedIncludeEnd =
    typeof c?.includeEnd === "boolean"
      ? c.includeEnd
      : isBetween && typeof inputPayload?.includeEnd === "boolean"
      ? inputPayload.includeEnd
      : true;

  // Derive fieldMode for legacy Jump To rules without fieldMode
  let hydratedFieldMode: FieldMode | undefined = c?.fieldMode as FieldMode | undefined;
  if (!hydratedFieldMode && store.surveyId && props.type === "jumpTo") {
    const slideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
    const selected = String(c?.selectedField || "");
    hydratedFieldMode = selected && slideIds.has(selected) ? "slide" : "field";
  }

  return {
    selectedField: c?.selectedField ?? null,
    selectedOperation: c?.selectedOperation ?? null,
    inputValue: c?.inputValue ?? null,
    rangeStart: hydratedRangeStart,
    rangeEnd: hydratedRangeEnd,
    includeStart: hydratedIncludeStart,
    includeEnd: hydratedIncludeEnd,
    fieldMode: hydratedFieldMode ?? (c?.fieldMode as FieldMode | undefined) ?? undefined,
    dateFormat: c?.dateFormat ?? undefined,
  };
};

const initialRawConditions =
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.conditions as any[]) || [];

const conditions = ref<ConditionRow[]>(
  initialRawConditions.length
    ? initialRawConditions.map((c) => toConditionRow(c))
    : [
        {
          selectedField: null,
          selectedOperation:
            props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
          inputValue: null,
          rangeStart: undefined as any,
          rangeEnd: undefined as any,
          includeStart: true,
          includeEnd: true,
          fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
        },
      ]
);

// Container for teleporting embed modals (image/link) inside the conditional logic modal
const clEditorContainer = ref<HTMLElement | null>(null);

const addCondition = (condition, index) => {
  sharedSelectedOperation.value = condition;
  if (condition !== "then" && index === conditions.value.length - 1) {
    conditions.value.push({
      selectedField: null,
      selectedOperation:
        props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const deleteCondition = (index) => {
  conditions.value.splice(index, 1);
  if (conditions.value.length === 0) {
    conditions.value.push({
      selectedField: null,
      selectedOperation: null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const isConditionInvalid = (condition) => {
  // Jump To (Slide basis): only slide selection is required; no value input
  if (props.type === "jumpTo" && store.surveyId && condition.fieldMode === "slide") {
    return !condition.selectedField;
  }
  if ([IS_EMPTY, IS_FILLED, "isChecked", "isNotChecked"].includes(condition.selectedOperation)) {
    return false;
  }
  if (condition.selectedOperation === "between") {
    const hasStart =
      condition.rangeStart !== undefined &&
      condition.rangeStart !== null &&
      condition.rangeStart !== "";
    const hasEnd =
      condition.rangeEnd !== undefined && condition.rangeEnd !== null && condition.rangeEnd !== "";
    return !(condition.selectedField && condition.selectedOperation && hasStart && hasEnd);
  }
  return !(
    condition.selectedField &&
    condition.selectedOperation &&
    (condition.inputValue || condition.inputValue === 0)
  );
};

const isSaveDisabled = computed(
  () =>
    conditions.value.some((condition) => {
      // Jump To (Slide basis): valid when slide is chosen
      if (props.type === "jumpTo" && store.surveyId && condition.fieldMode === "slide") {
        return !condition.selectedField;
      }
      if (
        [IS_EMPTY, IS_FILLED, "isChecked", "isNotChecked"].includes(condition.selectedOperation)
      ) {
        return false;
      }
      if (condition.selectedOperation === "between") {
        const hasStart =
          condition.rangeStart !== undefined &&
          condition.rangeStart !== null &&
          condition.rangeStart !== "";
        const hasEnd =
          condition.rangeEnd !== undefined &&
          condition.rangeEnd !== null &&
          condition.rangeEnd !== "";
        return !(condition.selectedField && condition.selectedOperation && hasStart && hasEnd);
      }
      return !(
        condition.selectedField &&
        condition.selectedOperation &&
        (condition.inputValue || condition.inputValue === 0)
      );
    }) ||
    (props.type !== "jumpTo" &&
      !Array.isArray(outcomeValue.value) &&
      outcomeValue.value.trim() === "") ||
    (props.type === "jumpTo" && !jumpTarget.value) ||
    !!cycleErrorMessage.value
);

// --- Cycle protection (Jump To rules) ---
const getSlideLabel = (id: string): string => {
  const index = store.slides.findIndex((s: any) => String(s.id) === String(id));
  if (index === -1) {
    return String(id);
  }
  return `P${index + 1} - ${store.slides[index].slideName}`;
};

const getSlideIdsFromRule = (rule: any): string[] => {
  if (!rule || !Array.isArray(rule?.conditions)) {
    return [];
  }
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return rule.conditions
    .filter((cond: any) => {
      const sid = String(cond?.selectedField || "");
      return !!sid && (cond?.fieldMode === "slide" || allSlideIds.has(sid));
    })
    .map((cond: any) => String(cond.selectedField));
};

const computeIfSlideIds = (): string[] => {
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return (conditions.value || [])
    .filter((c: any) => {
      const sid = String(c?.selectedField || "");
      return (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.has(sid));
    })
    .map((c: any) => String(c.selectedField));
};

const willIntroduceTwoWayCycle = (
  sources: string[],
  target: string
): { a: string; b: string } | null => {
  if (!store.surveyId || !target || !sources?.length) {
    return null;
  }
  const edges = new Map<string, string>();
  const rulesArr: any[] = ((store.builderConfig as any)?.conditionalLogic || []) as any[];
  // existing edges
  rulesArr.forEach((r) => {
    if (r?.outcome?.type !== "jumpTo") {
      return;
    }
    const rTarget = String(r?.outcome?.jumpTarget || "");
    if (!rTarget) {
      return;
    }
    const rSources = getSlideIdsFromRule(r);
    rSources.forEach((s) => edges.set(String(s), rTarget));
  });
  // proposed edges
  sources.forEach((s) => edges.set(String(s), String(target)));
  // detect A<->B
  for (const [a, b] of edges.entries()) {
    if (!b) {
      continue;
    }
    const back = edges.get(String(b));
    if (back === a) {
      return { a, b };
    }
  }
  return null;
};

const cycleErrorMessage = ref<string>("");

watchEffect(() => {
  if (props.type !== "jumpTo" || !store.surveyId) {
    cycleErrorMessage.value = "";
    return;
  }
  const sources = computeIfSlideIds();
  const target = String(jumpTarget.value || "");
  const pair = willIntroduceTwoWayCycle(sources, target);
  if (pair) {
    cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
      a: getSlideLabel(pair.a),
      b: getSlideLabel(pair.b),
    }) as string;
  } else {
    cycleErrorMessage.value = "";
  }
});

const onSave = () => {
  if (props.type === "jumpTo" && store.surveyId) {
    const sources = computeIfSlideIds();
    const target = String(jumpTarget.value || "");
    const pair = willIntroduceTwoWayCycle(sources, target);
    if (pair) {
      cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
        a: getSlideLabel(pair.a),
        b: getSlideLabel(pair.b),
      }) as string;
      return; // block save
    }
  }
  conditions.value = conditions.value
    .filter((condition: any) => condition.conditionalOperation !== null)
    .map((condition) => {
      if (condition.selectedOperation === "between") {
        const payloadDateFormat = dateFormatForSave(condition);
        const betweenPayload = {
          start: normalizeDateConditionValueForSave(condition, condition.rangeStart),
          end: normalizeDateConditionValueForSave(condition, condition.rangeEnd),
          includeStart: !!condition.includeStart,
          includeEnd: !!condition.includeEnd,
        };
        return {
          selectedField: condition.selectedField,
          selectedOperation: condition.selectedOperation,
          inputValue: betweenPayload as any,
          ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
        } as any;
      }
      if (getType(condition) === "monetory" && !isNaN(parseFloat(condition.inputValue))) {
        const payloadDateFormat = dateFormatForSave(condition);
        return {
          selectedField: condition.selectedField,
          selectedOperation: condition.selectedOperation,
          inputValue: parseFloat(condition.inputValue).toFixed(2),
          ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
        } as any;
      }
      const payloadDateFormat = dateFormatForSave(condition);
      return {
        selectedField: condition.selectedField,
        selectedOperation: condition.selectedOperation,
        inputValue: normalizeDateConditionValueForSave(condition, condition.inputValue),
        ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
      } as any;
    });

  const savedLogic: any = {
    conditions: conditions.value,
    outcome: { type: props.type, value: outcomeValue.value },
    conditionalOperation: sharedSelectedOperation.value,
  };
  if (props.type === "disqualifyLead") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      disqualifyAction: disqualifyAction.value,
    };

    if (store.surveyId) {
      savedLogic.outcome = {
        ...savedLogic.outcome,
        disqualifyTiming: disqualifyTiming.value,
      };
    }
  }
  if (props.type === "showHideFields") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      hideType: hideType.value,
    };
  }
  if (props.type === "jumpTo") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      jumpTarget: jumpTarget.value,
      jumpTargetType: store.surveyId ? "slide" : jumpTargetType.value,
    };
    // Two-way sync to sidebar jumpTo when basis is Slide
    if (store.surveyId) {
      const allSlideIds = store.slides.map((s) => String(s.id));
      const slideIds = (conditions.value || [])
        .filter((c: any) => {
          const sid = String(c?.selectedField || "");
          return (
            (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.includes(sid))
          );
        })
        .map((c: any) => String(c.selectedField));
      slideIds.forEach((sid: string) => {
        const slideIndex = store.slides.findIndex((s) => s.id === sid);
        if (slideIndex >= 0) {
          (store.slides[slideIndex] as any).jumpTo = jumpTarget.value;
        }
      });

      // De-duplicate Jump To rules per slide: update existing rule if one already exists
      const rulesArr: any[] = (store.builderConfig as any)?.conditionalLogic || [];
      slideIds.forEach((sid: string) => {
        const matchingIndexes = rulesArr
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => {
            if (r?.outcome?.type !== "jumpTo" || !Array.isArray(r?.conditions)) {
              return false;
            }
            return r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && c?.selectedField === sid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === sid)
            );
          })
          .map(({ i }) => i);

        if (matchingIndexes.length) {
          const keepIndex = Math.min(...matchingIndexes);
          // Update the kept rule's outcome to latest target
          rulesArr[keepIndex].outcome = {
            ...(rulesArr[keepIndex].outcome || {}),
            jumpTarget: jumpTarget.value,
            jumpTargetType: "slide",
            type: "jumpTo",
          };
          // Remove other duplicates except the kept one
          const toRemove = matchingIndexes.filter((idx) => idx !== keepIndex);
          toRemove
            .sort((a, b) => b - a)
            .forEach((idx) => {
              if (idx >= 0 && idx < rulesArr.length) {
                rulesArr.splice(idx, 1);
              }
            });
        }
      });
      // Persist potential dedup changes
      (store.builderConfig as any).conditionalLogic = rulesArr;

      // Upsert: write savedLogic into existing Jump To rule for the same slide if present
      let writeIndex = props.editIndex as number;
      const firstSid = slideIds[0];
      if (firstSid) {
        const existingIdx = ((store.builderConfig as any).conditionalLogic || []).findIndex(
          (r: any) =>
            r?.outcome?.type === "jumpTo" &&
            Array.isArray(r?.conditions) &&
            r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && String(c?.selectedField) === firstSid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === firstSid)
            )
        );
        if (existingIdx >= 0) {
          writeIndex = existingIdx;
        }
      }
      (store.builderConfig as any).conditionalLogic[writeIndex] = savedLogic;
      store.anyUnsavedChanges = true;
      console.log("savedLogic", store.builderConfig.conditionalLogic);
      emit("onSave");
      store.addToHistory();
      return;
    }
  }
  store.builderConfig.conditionalLogic[props.editIndex] = savedLogic;
  store.anyUnsavedChanges = true;
  console.log("savedLogic", store.builderConfig.conditionalLogic);
  emit("onSave");
  store.addToHistory();
};

const clonedFields = computed(() => {
  let fields: ConditionalSourceField[] = [];

  if (store.formId) {
    fields = cloneDeep(store.elements || []);
  } else if (store.surveyId) {
    fields = (store.slides || []).reduce(
      (acc: ConditionalSourceField[], slide: { slideData?: ConditionalSourceField[] }) => {
        if (slide.slideData && Array.isArray(slide.slideData)) {
          return [...acc, ...slide.slideData];
        }
        return acc;
      },
      []
    );
  }

  // Handle address settings for both forms and surveys
  const groupAddressIndex = fields.findIndex((item) => item.tag === "group_address");
  if (groupAddressIndex !== -1) {
    fields.splice(groupAddressIndex, 1, ...(store.addressSettings?.children || []));
  }

  return fields;
});

const fieldOptions = computed(() => {
  return clonedFields.value
    .filter(({ type }) => {
      if (type === "url") {
        return false;
      }
      if (props.type === "jumpTo" || (props.type === "disqualifyLead" && store.surveyId)) {
        // expanded coverage for jumpTo & survey disqualify
        const excluded = [
          ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
          "h1",
          "html",
          "img",
          "captcha",
          "source",
        ];
        return !excluded.includes(type);
      }

      const defaultExcludes = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        ...(standardFields?.integration?.group || []).map((x) => x.type),
        "textbox_list",
        "h1",
        "html",
        "img",
        "captcha",
        "source",
      ];
      return !defaultExcludes.includes(type);
    })
    .flatMap((item) => {
      const iconRenderer = () => getOptionIconVNode(item as any);
      // Split T&C into TnC1 and TnC2 when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            type: item.type,
            conditionalFieldKind: resolveConditionalFieldKind(item),
            dataType: item.dataType,
            format: item.format,
            separator: item.separator,
            timeFormat: item.timeFormat,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            type: item.type,
            conditionalFieldKind: resolveConditionalFieldKind(item),
            dataType: item.dataType,
            format: item.format,
            separator: item.separator,
            timeFormat: item.timeFormat,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }
      const isPictureChoice = isPictureChoiceField(item);
      return [
        {
          value: item.hiddenFieldQueryKey,
          label: item.label,
          type: isPictureChoice
            ? item.dataType === "CHECKBOX"
              ? "checkbox"
              : "radio"
            : item.type,
          conditionalFieldKind: isPictureChoice
            ? item.dataType === "CHECKBOX"
              ? "checkbox"
              : "radio"
            : resolveConditionalFieldKind(item),
          dataType: item.dataType,
          format: item.format,
          separator: item.separator,
          timeFormat: item.timeFormat,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
          picklistOptionsImage: isPictureChoice ? item.picklistOptionsImage || [] : null,
          isPictureChoice,
          allowCustomOption: !!(item as any)?.allowCustomOption,
        },
      ];
    });
});

const showHideFieldOptions = computed(() => {
  return clonedFields.value
    .filter(({ type }) => {
      const excluded = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        // Exclude captcha from Show/Hide targets
        "captcha",
      ];
      return !excluded.includes(type);
    })
    .flatMap((item) => {
      // Use a renderer function to generate a fresh icon VNode per render
      const iconRenderer = () => getOptionIconVNode(item as any);
      const rawLabel = item.label;
      const plainLabel = (item.label || "").replace(/<\/?[^>]+(>|$)/g, "");
      const truncate = (s: string, max = 32) => (s && s.length > max ? s.slice(0, max) + "…" : s);

      // Split T&C into two targets when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            rawLabel: "TnC1",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            rawLabel: "TnC2",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }

      return [
        {
          value: item.hiddenFieldQueryKey,
          label: truncate(plainLabel),
          rawLabel,
          type: item.type,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
        },
      ];
    });
});

// Jump To target type (Question or Slide)
const jumpTargetType = ref(
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome as any)?.jumpTargetType ||
    "question"
);

const questionOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return clonedFields.value.map((item) => ({ value: item.hiddenFieldQueryKey, label: item.label }));
});

// IF (basis) slide options: no "Submit Survey"
const ifSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return store.slides.map((slide, index) => ({
    value: slide.id,
    label: `P${index + 1} - ${slide.slideName}`,
  }));
});

// Jump target slide options: exclude any selected slide(s) in IF; no "Submit Survey"
const jumpSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  const excluded = new Set(
    (conditions.value || [])
      .filter((c: any) => c?.fieldMode === "slide" && c?.selectedField)
      .map((c: any) => String(c.selectedField))
  );
  return store.slides
    .map((slide, index) => ({ value: slide.id, label: `P${index + 1} - ${slide.slideName}` }))
    .filter((opt) => !excluded.has(String(opt.value)));
});

const operationOptions = ref(allOperationOptions);
const fieldOptionByValue = computed(
  () => new Map(fieldOptions.value.map((option) => [option.value, option]))
);

const getSelectedFieldOption = (condition): ConditionalFieldOption | undefined => {
  if (!condition.selectedField) {
    return undefined;
  }
  return fieldOptionByValue.value.get(condition.selectedField);
};

const getType = (condition) => {
  return getSelectedFieldOption(condition)?.conditionalFieldKind || null;
};

const isPictureChoiceCondition = (condition): boolean =>
  getSelectedFieldOption(condition)?.isPictureChoice === true;

const getPictureChoicePicklistOptions = (
  selectedOption: ConditionalFieldOption
) => {
  const imageOptions = selectedOption.picklistOptionsImage ?? [];

  if (imageOptions.length) {
    return imageOptions.map((item) => ({
      value: item.label,
      label: item.label,
      imageSrc: item.thumbnailUrl || item.src || emptyOptionImage,
      isPictureChoice: true,
    }));
  }

  return (selectedOption.picklistOptions ?? []).map((item) => ({
    value: item,
    label: item,
    imageSrc: emptyOptionImage,
    isPictureChoice: true,
  }));
};

const getPicklistOptions = (condition) => {
  if (!condition.selectedField) {
    return [];
  }
  const selectedOption = getSelectedFieldOption(condition);
  const baseOptions = selectedOption?.isPictureChoice
    ? getPictureChoicePicklistOptions(selectedOption)
    : selectedOption?.picklistOptions?.map((item) => ({ value: item, label: item })) || [];
  const selectedType = getType(condition);
  const normalizedSelectedType =
    typeof selectedType === "string" ? selectedType.toLowerCase() : selectedType;
  if (
    (normalizedSelectedType === "radio" ||
      normalizedSelectedType === "checkbox" ||
      selectedOption?.isPictureChoice) &&
    !!selectedOption?.allowCustomOption
  ) {
    return [
      ...baseOptions,
      selectedOption.isPictureChoice
        ? {
            value: "Other",
            label: t("common.other"),
            imageSrc: otherOptionImage,
            isPictureChoice: true,
          }
        : { value: "Other", label: t("common.other") },
    ];
  }
  return baseOptions;
};

const updateOptions = (condition, value, oldSelectedField = null) => {
  if (!value) {
    return;
  }

  const oldSelectedType = oldSelectedField
    ? fieldOptionByValue.value.get(oldSelectedField)?.conditionalFieldKind
    : null;
  const selectedFieldOption = fieldOptionByValue.value.get(value);
  const selectedType = selectedFieldOption?.conditionalFieldKind;

  if (oldSelectedField && oldSelectedType !== selectedType) {
    condition.inputValue = "";
    if (
      selectedType &&
      ["single_options", "radio", "checkbox", "multiple_options"].includes(selectedType)
    ) {
      condition.inputValue = getPicklistOptions(condition)?.[0]?.value || "";
    }
    if (selectedType === "date") {
      condition.inputValue = dayjs().format("YYYY-MM-DD");
    }
    condition.rangeStart = undefined;
    condition.rangeEnd = undefined;
    condition.includeStart = true;
    condition.includeEnd = true;
    condition.dateFormat = undefined;
  }

  showPhoneAlert.value = selectedType === "phone" || value === "phone";

  // Use the same operation filtering for all types (including disqualifyLead)
  operationOptions.value = allOperationOptions.filter((option) =>
    selectedType ? option.types.includes(selectedType) : false
  );

  // Ensure selected operation is valid for the current options
  if (!operationOptions.value.some((o) => o.value === condition.selectedOperation)) {
    condition.selectedOperation = operationOptions.value[0]?.value || null;
  }
};

const hideInput = (condition) => shouldHideInput(condition.selectedOperation);

const resolveOptionIcon = (option: any): VNode | null => {
  try {
    const maybe = typeof option.icon === "function" ? option.icon() : option.icon;
    return maybe || null;
  } catch {
    return null;
  }
};

const renderFieldOption = ({ node, option }: { node: VNode; option: any }) =>
  h("div", { class: "relative flex items-center w-full" }, [
    h(
      "div",
      {
        class: [
          "option-icon ml-2 shrink-0 flex items-center justify-center",
          option.type === "rating" ? "w-8 h-8 ml-[4px] mr-[-4px]" : "w-6 h-6",
        ].join(" "),
      },
      [resolveOptionIcon(option)]
    ),
    h("div", { class: "w-full" }, [node]),
  ]);

const renderOptions = ({ node, option }: { node: VNode; option }) =>
  h(
    UITooltip,
    { placement: "left-start" },
    {
      trigger: () => node,
      default: () => option.label,
    }
  );

const renderPictureChoiceOptionLabel = (option) => {
  const label = String(option.label ?? "");

  return h("div", { class: "picture-choice-condition-label flex min-w-0 items-center gap-2 py-2" }, [
    h("img", {
      src: option.imageSrc || emptyOptionImage,
      alt: "",
      "aria-hidden": "true",
      class:
        "picture-choice-condition-thumbnail h-10 w-10 shrink-0 rounded object-cover",
    }),
    h(
      "span",
      { class: "min-w-0 truncate text-base font-medium text-gray-900" },
      label
    ),
  ]);
};
/** Use condition's stored format when setting datepicker value (parsing). */
const dateFormatForParsing = (condition: any) =>
  dateTypeFieldFormatDayjsForCondition(store, condition);

const dateTypeFieldValue = (condition: any) =>
  getDateTypeFieldValue(store, condition.selectedField, condition.inputValue, condition.dateFormat);

const dateFormatUI = (condition: any) => resolveDateTypeFieldFormat(store, condition.selectedField);

/** Use store format when writing date to condition; also set condition.dateFormat to this. */
const dateFormatDayjs = (condition: any) =>
  resolveDateTypeFieldFormatDayjs(store, condition.selectedField);

const dateFormatForSave = (condition: ConditionRow): string | undefined => {
  const selectedType = getType(condition);
  if (selectedType === "date" || selectedType === "date_time") {
    return dateFormatDayjs(condition);
  }
  return condition?.dateFormat;
};

const normalizeDateConditionValueForSave = (
  condition: ConditionRow,
  value: unknown
): unknown => {
  if (getType(condition) !== "date" || value === undefined || value === null || value === "") {
    return value;
  }
  // Only a picker timestamp or a stored date string can be reformatted; anything
  // else round-trips untouched rather than becoming an Invalid Date.
  if (typeof value !== "number" && typeof value !== "string") {
    return value;
  }

  const parsed =
    typeof value === "number" ? dayjs(value) : dayjs(value, dateFormatForParsing(condition));
  return parsed.isValid() ? parsed.format(dateFormatDayjs(condition)) : value;
};

const safeDayjs = (date: string, condition: any) => {
  const value = dayjs(date, dateFormatForParsing(condition));
  return value.isValid() ? value.valueOf() : null;
};

const timeFormat = (condition: ConditionRow): string | number | undefined =>
  getSelectedFieldOption(condition)?.timeFormat;

const timePickerFormat = (condition: ConditionRow): string => timeFormatForUI(timeFormat(condition));
const temporalPickerPlacement = ["bottom", "end"].join("-");

const isJumpToSurveyCondition = (): boolean => props.type === "jumpTo" && !!store.surveyId;

const isSlideBasisCondition = (condition: ConditionRow): boolean =>
  isJumpToSurveyCondition() && condition.fieldMode === "slide";

const isDateTimeBetweenValueCondition = (condition: ConditionRow): boolean =>
  !isJumpToSurveyCondition() &&
  !isSlideBasisCondition(condition) &&
  getType(condition) === "date_time" &&
  condition.selectedOperation === "between";

const isDateTimeSingleValueCondition = (condition: ConditionRow): boolean =>
  !isJumpToSurveyCondition() &&
  !isSlideBasisCondition(condition) &&
  getType(condition) === "date_time" &&
  condition.selectedOperation !== "between";

const conditionRowClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    return condition.fieldMode === "slide"
      ? "mb-8 grid grid-cols-2 items-center gap-x-2"
      : "mb-8 grid grid-cols-4 items-center gap-x-2";
  }

  return isDateTimeSingleValueCondition(condition)
    ? "mb-8 flex items-center gap-x-2 px-0.5"
    : isDateTimeBetweenValueCondition(condition)
    ? "mb-8 flex flex-wrap items-center gap-x-2 gap-y-2 px-0.5"
    : "mb-8 flex gap-x-2 px-0.5";
};

const selectedFieldClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    return "w-full";
  }

  if (isDateTimeBetweenValueCondition(condition)) {
    return "w-[calc(50%-0.25rem)]";
  }

  if (condition.selectedOperation === "between") {
    return "w-1/4";
  }

  return isDateTimeSingleValueCondition(condition) ? "min-w-0 flex-1" : "w-full";
};

const selectedOperationClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    return "w-full";
  }

  if (isDateTimeBetweenValueCondition(condition)) {
    return "w-[calc(50%-0.25rem)]";
  }

  if (condition.selectedOperation === "between") {
    return "w-1/4";
  }

  return isDateTimeSingleValueCondition(condition) ? "w-[10.5rem] shrink-0" : "w-full";
};

const dateTimeSingleValueClass = (condition: ConditionRow): string[] => [
  hideInput(condition) ? "invisible" : "visible",
  isDateTimeSingleValueCondition(condition) ? "flex w-[21.5rem] shrink-0 gap-2" : "flex w-full gap-2",
];

const dateTimeBetweenValueClass = (condition: ConditionRow): string =>
  isDateTimeBetweenValueCondition(condition)
    ? "grid w-full grid-cols-2 gap-2"
    : props.type === "jumpTo" && store.surveyId
    ? "grid w-full grid-cols-2 gap-2"
    : "grid w-1/2 grid-cols-2 gap-2";

const dateTimeBetweenPickerClass = "conditional-temporal-picker min-w-0 flex-1";
const dateTimeSingleDatePickerClass = "conditional-temporal-picker min-w-0 flex-[1.08_1_0]";
const dateTimeSingleTimePickerClass = "conditional-temporal-picker min-w-0 flex-1";

const timePickerValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return value;
  }
  const timeValue = toConditionTimeValue(value);
  if (!timeValue) {
    return null;
  }
  return toTimePickerTimestamp(timeValue, "24");
};

const dateTimeDatePickerValue = (value: unknown): number | null => {
  const parsed = parseConditionDateTimeValue(value);
  return parsed.dateValue;
};

const dateTimeTimePickerValue = (value: unknown): number | null => {
  const parsed = parseConditionDateTimeValue(value);
  return parsed.timeValue;
};

const defaultDateTimeDateValue = (): number => dayjs().startOf("day").valueOf();

const defaultDateTimeTimeValue = (): number => new Date(2000, 0, 1, 0, 0, 0, 0).getTime();

const mergeDateTimeValue = (
  currentValue: unknown,
  dateValue?: number | null,
  timeValue?: number | null
): string => {
  const hasDateUpdate = dateValue !== undefined;
  const hasTimeUpdate = timeValue !== undefined;
  const currentDate = dateTimeDatePickerValue(currentValue);
  const currentTime = dateTimeTimePickerValue(currentValue);
  const shouldDefaultDate = hasTimeUpdate && timeValue !== null;
  const shouldDefaultTime = hasDateUpdate && dateValue !== null;
  const nextDate = hasDateUpdate
    ? dateValue
    : currentDate ?? (shouldDefaultDate ? defaultDateTimeDateValue() : null);
  const nextTime = hasTimeUpdate
    ? timeValue
    : currentTime ?? (shouldDefaultTime ? defaultDateTimeTimeValue() : null);
  return toConditionDateTimeValue(nextDate, nextTime);
};
</script>

<template>
  <div class="divider h-[610px] overflow-auto">
    <div class="flex justify-between py-3 font-medium">
      {{ t("form.conditonalLogic.logic.if") }}
      <Trash02Icon
        v-if="conditions.length > 1"
        class="mr-3 h-4 w-4 cursor-pointer text-error-700"
        @click="deleteCondition(0)"
      />
    </div>
    <template v-for="(condition, index) in conditions" :key="index">
      <div class="mb-2 flex flex-col gap-y-2 text-base">
        <div :class="conditionRowClass(condition)">
          <!-- Jump To (Surveys): Field or Slide basis selector -->
          <UISelect
            v-if="props.type === 'jumpTo' && store.surveyId"
            :id="`fieldMode-${index}`"
            class="w-full"
            :value="condition.fieldMode || 'field'"
            :options="[
              { label: t('form.conditonalLogic.field'), value: 'field' },
              { label: t('form.conditonalLogic.slide'), value: 'slide' },
            ]"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldOrSlide')"
            filterable
            @update:value="
              (v) => {
                condition.fieldMode = v;
                condition.selectedField = null;
                condition.inputValue = null;
                condition.rangeStart = undefined;
                condition.rangeEnd = undefined;
                condition.includeStart = true;
                condition.includeEnd = true;
                condition.dateFormat = undefined;
                condition.selectedOperation = 'isEqualTo';
              }
            "
          />
          <UISelect
            v-if="!(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide')"
            :id="`selectedField-${index}`"
            :class="selectedFieldClass(condition)"
            :value="condition.selectedField"
            :options="fieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('form.conditonalLogic.selectField')"
            filterable
            @update:value="
              (value) => {
                const oldSelectedField = condition.selectedField;
                condition.selectedField = value;
                updateOptions(condition, value, oldSelectedField);
                condition.selectedOperation = operationOptions[0].value;
              }
            "
          />
          <!-- Slide chooser when basis is Slide -->
          <UISelect
            v-else
            :id="`selectedSlide-${index}`"
            class="w-full"
            :value="condition.selectedField"
            :options="ifSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
            @update:value="
              (value) => {
                condition.selectedField = value;
                // No value input is required for slide-level IF
              }
            "
          />
          <!-- For jumpTo, operation is implicitly isEqualTo; no selector needed -->
          <UISelect
            v-if="
              props.type !== 'jumpTo' ||
              (props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'field')
            "
            :id="`selectedOperation-${index}`"
            :class="selectedOperationClass(condition)"
            :value="condition.selectedOperation"
            :options="operationOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldState')"
            filterable
            :disabled="!condition.selectedField"
            @click="updateOptions(condition, condition.selectedField)"
            @update:value="(value) => (condition.selectedOperation = value)"
          />
          <!-- Value input renders only when basis is Field -->
          <UIDatepicker
            v-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date' &&
              condition.selectedOperation === 'between'
            "
            :id="`input_datepicker_range-${index}`"
            :class="[
              'conditional-temporal-picker',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-1/2',
            ]"
            type="daterange"
            :placement="temporalPickerPlacement"
            :shortcuts="dateRangeShortcuts()"
            :value="
              (condition.rangeStart && condition.rangeEnd && safeDayjs(condition.rangeStart, condition) && safeDayjs(condition.rangeEnd, condition)
                ? [
                    safeDayjs(condition.rangeStart, condition),
                    safeDayjs(condition.rangeEnd, condition),
                  ]
                : null) as any
            "
            :format="dateFormatUI(condition)"
            @update:value="
              (values) => {
                const [start, end] = values || [];
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.rangeStart = start ? dayjs(start).format(format) : undefined;
                condition.rangeEnd = end ? dayjs(end).format(format) : undefined;
              }
            "
          />
          <UIDatepicker
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date'
            "
            id="input_datepicker"
            :class="[
              'conditional-temporal-picker',
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="dateTypeFieldValue(condition)"
            :format="dateFormatUI(condition)"
            :placement="temporalPickerPlacement"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="
              (value) => {
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.inputValue = dayjs(value).format(format);
              }
            "
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'time' &&
              condition.selectedOperation === 'between'
            "
            :class="
              props.type === 'jumpTo' && store.surveyId ? 'flex w-full gap-2' : 'flex w-1/2 gap-2'
            "
          >
            <UITimePicker
              :id="`inputTimeStart-${index}`"
              class="conditional-temporal-picker w-1/2"
              :value="timePickerValue(condition.rangeStart)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="(value) => (condition.rangeStart = toConditionTimeValue(value))"
            />
            <UITimePicker
              :id="`inputTimeEnd-${index}`"
              class="conditional-temporal-picker w-1/2"
              :value="timePickerValue(condition.rangeEnd)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="(value) => (condition.rangeEnd = toConditionTimeValue(value))"
            />
          </div>
          <UITimePicker
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'time'
            "
            :id="`inputTime-${index}`"
            :class="[
              'conditional-temporal-picker',
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="timePickerValue(condition.inputValue)"
            :format="timePickerFormat(condition)"
            :placement="temporalPickerPlacement"
            :placeholder="t('common.dateTime.timePlaceholder')"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="(value) => (condition.inputValue = toConditionTimeValue(value))"
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date_time' &&
              condition.selectedOperation === 'between'
            "
            :class="dateTimeBetweenValueClass(condition)"
          >
            <div class="flex min-w-0 gap-2">
              <UIDatepicker
                :id="`inputDateTimeStartDate-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeDatePickerValue(condition.rangeStart)"
                :format="dateFormatUI(condition)"
                :placement="temporalPickerPlacement"
                :aria-label="t('common.dateTime.rangeStart')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) => {
                    condition.dateFormat = dateFormatDayjs(condition);
                    condition.rangeStart = mergeDateTimeValue(condition.rangeStart, value, undefined);
                  }
                "
              />
              <UITimePicker
                :id="`inputDateTimeStartTime-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeTimePickerValue(condition.rangeStart)"
                :format="timePickerFormat(condition)"
                :placement="temporalPickerPlacement"
                :placeholder="t('common.dateTime.timePlaceholder')"
                :aria-label="t('common.dateTime.timeStart')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) =>
                    (condition.rangeStart = mergeDateTimeValue(
                      condition.rangeStart,
                      undefined,
                      value
                    ))
                "
              />
            </div>
            <div class="flex min-w-0 gap-2">
              <UIDatepicker
                :id="`inputDateTimeEndDate-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeDatePickerValue(condition.rangeEnd)"
                :format="dateFormatUI(condition)"
                :placement="temporalPickerPlacement"
                :aria-label="t('common.dateTime.rangeEnd')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) => {
                    condition.dateFormat = dateFormatDayjs(condition);
                    condition.rangeEnd = mergeDateTimeValue(condition.rangeEnd, value, undefined);
                  }
                "
              />
              <UITimePicker
                :id="`inputDateTimeEndTime-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeTimePickerValue(condition.rangeEnd)"
                :format="timePickerFormat(condition)"
                :placement="temporalPickerPlacement"
                :placeholder="t('common.dateTime.timePlaceholder')"
                :aria-label="t('common.dateTime.timeEnd')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) =>
                    (condition.rangeEnd = mergeDateTimeValue(
                      condition.rangeEnd,
                      undefined,
                      value
                    ))
                "
              />
            </div>
          </div>
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date_time'
            "
            :class="dateTimeSingleValueClass(condition)"
          >
            <UIDatepicker
              :id="`inputDateTimeDate-${index}`"
              :class="dateTimeSingleDatePickerClass"
              :value="dateTimeDatePickerValue(condition.inputValue)"
              :format="dateFormatUI(condition)"
              :placement="temporalPickerPlacement"
              :aria-label="t('common.dateTime.rangeStart')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="
                (value) => {
                  condition.dateFormat = dateFormatDayjs(condition);
                  condition.inputValue = mergeDateTimeValue(condition.inputValue, value, undefined);
                }
              "
            />
            <UITimePicker
              :id="`inputDateTimeTime-${index}`"
              :class="dateTimeSingleTimePickerClass"
              :value="dateTimeTimePickerValue(condition.inputValue)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :aria-label="t('common.dateTime.timeFormatLabel')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="
                (value) =>
                  (condition.inputValue = mergeDateTimeValue(
                    condition.inputValue,
                    undefined,
                    value
                  ))
              "
            />
          </div>
          <UISelect
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              ['single_options', 'radio', 'checkbox', 'multiple_options'].includes(
                getType(condition)
              ) &&
              condition.selectedOperation !== 'between'
            "
            :id="`picklistOptions-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
              isPictureChoiceCondition(condition)
                ? 'picture-choice-condition-select'
                : '',
            ]"
            :value="condition.inputValue"
            :options="getPicklistOptions(condition)"
            :render-option="renderOptions"
            :render-label="
              isPictureChoiceCondition(condition)
                ? renderPictureChoiceOptionLabel
                : undefined
            "
            :placeholder="t('form.conditonalLogic.selectOption')"
            filterable
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="(value) => (condition.inputValue = value)"
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              condition.selectedOperation === 'between' &&
              ['numerical', 'monetory', 'score', 'rating'].includes(getType(condition))
            "
            :class="
              props.type === 'jumpTo' && store.surveyId ? 'flex w-full gap-2' : 'flex w-1/2 gap-2'
            "
          >
            <UIInput
              :id="`inputValueStart-${index}`"
              class="w-1/2"
              :model-value="condition.rangeStart"
              :placeholder="t('common.startValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeStart = value)"
            />
            <UIInput
              :id="`inputValueEnd-${index}`"
              class="w-1/2"
              :model-value="condition.rangeEnd"
              :placeholder="t('common.endValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeEnd = value)"
            />
          </div>
          <UIInput
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide')
            "
            :id="`inputValue-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :model-value="condition.inputValue"
            :placeholder="t('form.conditonalLogic.typeValueHere')"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:model-value="(value) => (condition.inputValue = value)"
          />
        </div>
        <div
          v-if="condition.selectedOperation === 'between'"
          class="-mt-6 mb-4 flex items-center gap-6 text-sm text-gray-700"
        >
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-start"
              :checked="condition.includeStart"
              @update:checked="(v) => (condition.includeStart = !!v)"
            />
            <span>{{
              includeStartText(
                condition,
                getType(condition),
                dateFormatUI(condition)?.toUpperCase(),
                timeFormat(condition)
              )
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-end"
              :checked="condition.includeEnd"
              @update:checked="(v) => (condition.includeEnd = !!v)"
            />
            <span>{{
              includeEndText(
                condition,
                getType(condition),
                dateFormatUI(condition)?.toUpperCase(),
                timeFormat(condition)
              )
            }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div :class="index === conditions.length - 1 ? 'w-[88%]' : 'w-3/5'">
            <div v-if="props.type === 'jumpTo'" class="font-medium text-gray-600">
              {{ t("form.conditonalLogic.logic.then") }}
            </div>
            <UITabs
              v-else
              default-value="then"
              type="segment"
              :value="index === conditions.length - 1 ? 'then' : sharedSelectedOperation"
              @update:value="(condition) => addCondition(condition, index)"
            >
              <UITabPane
                v-if="index === conditions.length - 1"
                name="then"
                :tab="t('form.conditonalLogic.logic.then')"
                >{{ t("form.conditonalLogic.logic.then") }}</UITabPane
              >
              <UITabPane
                name="and"
                :tab="t('form.conditonalLogic.logic.and')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.and") }}
              </UITabPane>
              <UITabPane
                name="or"
                :tab="t('form.conditonalLogic.logic.or')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.or") }}</UITabPane
              >
            </UITabs>
          </div>
          <Trash02Icon
            v-if="index !== conditions.length - 1"
            class="mr-3 h-4 w-4 cursor-pointer text-error-700"
            @click="deleteCondition(index + 1)"
          />
        </div>
      </div>
    </template>
    <UIAlert v-if="showPhoneAlert" id="warning-message" type="warning">
      <template #content>{{ t("form.conditonalLogic.phoneAlertMessage") }}</template>
    </UIAlert>
    <div id="output" class="mb-3 mt-3">
      <div class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-3 flex">
          <div v-if="props.type === 'disqualifyLead'" class="flex w-full gap-2 text-gray-900">
            <UISelect
              v-if="store.surveyId"
              id="disqualify-timing"
              v-model:value="disqualifyTiming"
              class="w-full"
              :options="disqualifyTimingOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
            />
            <UISelect
              :id="`disqualify-action`"
              v-model:value="disqualifyAction"
              class="w-full"
              :options="disqualifyActionOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
              @update:value="() => (outcomeValue = '')"
            />
          </div>
          <div v-if="props.type === 'displayCustomMessage'">
            {{ t("form.conditonalLogic.showCustomMessage") }}
          </div>
        </div>
        <div ref="clEditorContainer">
          <RichTextEditor
            v-if="
              props.type === 'displayCustomMessage' ||
              (props.type === 'disqualifyLead' && disqualifyAction === 'showCustomMessage')
            "
            v-model:modelValue="outcomeValue"
            size="mid"
            :is-submit-message="true"
            :modal-container="clEditorContainer"
          />
        </div>
      </div>
      <div
        v-if="
          props.type === 'redirectToUrl' ||
          (props.type === 'disqualifyLead' && disqualifyAction === 'openUrl')
        "
        class="mb-2 text-xs font-medium text-gray-700"
      >
        {{ t("form.conditonalLogic.openBelowUrl") }}
        <UIInput
          id="redirectToUrlInput"
          v-model="outcomeValue"
          class="my-3 w-full font-normal"
          :placeholder="t('common.exampleUrlPlaceholder')"
        />
        <div class="text-xs font-normal text-gray-600">
          {{ t("common.addUrlLike") }}<span class="underline">{{ URL_EXAMPLES.simple }}</span
          >, <span class="underline">{{ URL_EXAMPLES.https }}</span
          >, <span class="underline">{{ URL_EXAMPLES.withPath }}</span> {{ t("common.or") }}
          <span class="underline">{{ URL_EXAMPLES.fullPath }}</span
          >.
        </div>
      </div>
      <div v-if="props.type === 'showHideFields'" class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-8 flex gap-x-2">
          <UISelect
            id="showHideFieldType"
            v-model:value="hideType"
            class="w-1/3"
            :options="showHideOptions"
            :render-option="renderOptions"
            :placeholder="t('common.select')"
            filterable
          />
          <UISelect
            id="selectedFieldShowHide"
            v-model:value="outcomeValue"
            :options="showHideFieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('common.select')"
            filterable
            :multiple="showMultiple"
            type="default"
          />
        </div>
      </div>
      <div v-if="props.type === 'jumpTo'" class="mb-2 text-xs font-medium text-gray-700">
        {{ t("form.conditonalLogic.jumpToDescription") }}
        <div class="mb-8 mt-2 flex gap-x-2">
          <UISelect
            id="jumpToSlide"
            v-model:value="jumpTarget"
            class="w-full"
            :options="jumpSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
          />
        </div>

        <UIAlert v-if="cycleErrorMessage" id="cycle-error" type="error">
          <template #content>{{ cycleErrorMessage }}</template>
        </UIAlert>
      </div>
    </div>
  </div>
  <UIModalFooter
    id="modal-footer"
    :positive-text="t('common.save')"
    :disabled="isSaveDisabled"
    :loading="false"
    @positive-click="onSave"
  />
</template>

<style scoped>
.divider {
  border-bottom: 1px solid #eaecf0;
  width: 100%;
}
:deep(.icon-custom) {
  border: 0;
  box-shadow: none;
  display: block;
}
.n-tabs {
  width: 25%;
}
:deep(.n-tabs-tab) {
  border-radius: 6px !important;
}
.n-tab-pane {
  display: none;
}
:deep(.n-tabs-rail) {
  border-radius: 6px;
}
:deep(.n-avatar) {
  display: none !important;
}
:deep(.n-tag__content) {
  padding-left: 6px;
}

.conditional-temporal-picker :deep(.n-input) {
  min-height: 40px;
}

.conditional-temporal-picker :deep(.n-input-wrapper),
.conditional-temporal-picker :deep(.n-input__input),
.conditional-temporal-picker :deep(.n-input__suffix) {
  align-items: center;
}

.conditional-temporal-picker :deep(.n-input__suffix) {
  display: inline-flex;
  height: 100%;
  line-height: 1;
}

.conditional-temporal-picker :deep(.n-date-picker-icon),
.conditional-temporal-picker :deep(.n-time-picker-icon),
.conditional-temporal-picker :deep(.n-base-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.conditional-temporal-picker :deep(.n-date-picker-icon svg),
.conditional-temporal-picker :deep(.n-time-picker-icon svg),
.conditional-temporal-picker :deep(.n-base-icon svg) {
  display: block;
}

/* Normalize option icons to consistent size */
.option-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.option-icon :deep(svg),
.option-icon :deep(img) {
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  object-fit: contain;
}

.picture-choice-condition-select
  :deep(.n-base-selection-label__render-label .picture-choice-condition-thumbnail) {
  display: none;
}
.picture-choice-condition-select
  :deep(.n-base-selection-label__render-label .picture-choice-condition-label) {
  padding-bottom: 0;
  padding-top: 0;
}
</style>
